import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, dashboardPath } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  BookOpen, BookMarked, PenTool, FileSpreadsheet, Truck,
  LayoutDashboard, CheckCircle2, TrendingUp, Package,
} from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      nav(dashboardPath(user.role));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  const quickLogin = (em, pw) => { setEmail(em); setPassword(pw); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* -------- LEFT · Platform preview -------- */}
      <div className="hidden lg:flex relative bg-gradient-to-br from-brand-navy via-brand-blue to-brand-blueDark overflow-hidden">
        {/* Decorative grid + glow */}
        <div className="absolute inset-0 opacity-[0.08] bg-[radial-gradient(circle_at_1px_1px,white_1px,transparent_0)] [background-size:24px_24px]" />
        <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-brand-green/25 blur-3xl" />
        <div className="absolute -bottom-32 -right-24 w-96 h-96 rounded-full bg-brand-gold/20 blur-3xl" />

        <div className="relative flex flex-col justify-between p-12 xl:p-14 w-full text-white z-10">
          <Link to="/" aria-label="SchoolLane – Home" className="inline-block bg-white/95 rounded-lg px-3 py-2 shadow-lg w-fit">
            <Logo height={44} />
          </Link>

          <div className="mt-10 max-w-lg">
            <h2 className="text-4xl xl:text-5xl font-heading font-bold leading-tight">
              School Procurement<br/>Made Simple
            </h2>
            <p className="mt-4 text-white/80 text-base leading-relaxed">
              One workspace for schools, parents and vendors — book kits,
              stationery, uniforms, RFQs and delivery tracking.
            </p>
          </div>

          {/* Category tiles */}
          <div className="mt-10 grid grid-cols-3 gap-3 max-w-lg">
            {[
              { i: BookOpen,     l: "Academic Books" },
              { i: BookMarked,   l: "Library Books" },
              { i: PenTool,      l: "Stationery" },
              { i: Package,      l: "Book Kits" },
              { i: FileSpreadsheet, l: "School RFQs" },
              { i: Truck,        l: "Order Tracking" },
            ].map(({ i: Icon, l }) => (
              <div key={l} className="rounded-lg bg-white/10 backdrop-blur border border-white/15 px-3 py-3 text-xs font-semibold flex flex-col items-start gap-2 hover:bg-white/15 transition">
                <Icon size={16} className="text-brand-gold" />
                <span>{l}</span>
              </div>
            ))}
          </div>

          {/* Procurement dashboard preview card */}
          <div className="mt-8 rounded-2xl bg-white/95 text-slate-800 shadow-2xl border border-white/40 overflow-hidden max-w-lg">
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <LayoutDashboard size={14} className="text-brand-blue"/>
                Procurement Dashboard
              </div>
              <div className="flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"/>
                <span className="text-[10px] text-slate-500">Live</span>
              </div>
            </div>
            <div className="p-4 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[10px] text-slate-500 font-semibold">Open RFQs</div>
                <div className="text-xl font-heading font-bold text-brand-navy">12</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 font-semibold">In Transit</div>
                <div className="text-xl font-heading font-bold text-brand-blue">4</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 font-semibold">Delivered</div>
                <div className="text-xl font-heading font-bold text-emerald-600">27</div>
              </div>
            </div>
            <div className="px-4 pb-4 space-y-1.5 text-[11px]">
              <Row icon={CheckCircle2} tone="text-emerald-600" text="PO-01847 delivered · St. Michael's" />
              <Row icon={TrendingUp}   tone="text-brand-blue" text="Q3 stationery demand +18%" />
              <Row icon={FileSpreadsheet} tone="text-brand-gold" text="RFQ-00042 quoted" />
            </div>
          </div>

          <div className="text-white/60 text-xs">
            © {new Date().getFullYear()} SchoolLane · Confidential procurement platform
          </div>
        </div>
      </div>

      {/* -------- RIGHT · Login form -------- */}
      <div className="p-8 sm:p-14 flex items-center justify-center bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Logo height={44} />
          </div>
          <h1 className="text-3xl font-heading font-bold text-brand-navy">Sign in to SchoolLane</h1>
          <p className="text-slate-600 mt-2 text-sm">School Procurement Made Simple.</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Email</label>
              <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" className="mt-1 w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-brand-blue" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Password</label>
                <a href="/forgot-password" data-testid="login-forgot-link"
                  className="text-xs font-semibold text-brand-blue hover:text-brand-blueDark">
                  Forgot password?
                </a>
              </div>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" className="mt-1 w-full rounded-md border border-slate-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-brand-blue" />
            </div>
            <button disabled={loading} data-testid="login-submit" className="w-full rounded-md bg-brand-blue text-white font-semibold py-3 hover:bg-brand-blueDark transition-colors duration-200 disabled:opacity-60">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-6 rounded-md border border-slate-200 bg-white p-4">
            <div className="text-xs font-semibold text-slate-700 mb-2">Demo accounts (click to autofill)</div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">Approval chain</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button data-testid="quick-teacher" onClick={() => quickLogin("teacher@schoollane.in","SchoolAdm!n@2026")} className="rounded border border-slate-200 bg-white px-3 py-2 text-left hover:border-brand-blue">Teacher · Meera</button>
              <button data-testid="quick-depthead" onClick={() => quickLogin("depthead@schoollane.in","SchoolAdm!n@2026")} className="rounded border border-slate-200 bg-white px-3 py-2 text-left hover:border-brand-blue">Dept Head · Vikram</button>
              <button data-testid="quick-principal" onClick={() => quickLogin("school@schoollane.in","SchoolAdm!n@2026")} className="rounded border border-slate-200 bg-white px-3 py-2 text-left hover:border-brand-blue">Principal · Priya</button>
              <button data-testid="quick-accounts" onClick={() => quickLogin("accounts@schoollane.in","SchoolAdm!n@2026")} className="rounded border border-slate-200 bg-white px-3 py-2 text-left hover:border-brand-blue">Accounts · Neha</button>
            </div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-3 mb-1">Other roles</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <button data-testid="quick-parent" onClick={() => quickLogin("parent@schoollane.in","ParentUser@2026")} className="rounded border border-slate-200 bg-white px-3 py-2 text-left hover:border-brand-blue">Parent · Rahul</button>
              <button data-testid="quick-vendor" onClick={() => quickLogin("vendor@schoollane.in","VendorUser@2026")} className="rounded border border-slate-200 bg-white px-3 py-2 text-left hover:border-brand-blue">Vendor · BrightBooks</button>
            </div>
          </div>
          <div className="mt-6 text-sm text-slate-600 flex flex-wrap items-center gap-x-4 gap-y-2">
            <span>
              New here?{" "}
              <Link to="/register" data-testid="link-register" className="text-brand-blue font-semibold">
                Create an account
              </Link>
            </span>
            <span className="hidden sm:inline text-slate-300">·</span>
            <Link
              to="/contact"
              data-testid="link-book-demo"
              className="inline-flex items-center gap-1 text-brand-greenDark font-semibold hover:text-brand-green"
            >
              Book a Demo
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, tone, text }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon size={12} className={tone}/>
      <span className="text-slate-700">{text}</span>
    </div>
  );
}
